import { m } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import {
  containerVariants,
  itemVariants,
  viewPort,
} from '../../../utils/framer';

interface VideoClipProps {
  src: string;
  srcWebm?: string;
  poster?: string;
  autoplay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  width?: string;
  height?: string;
  title?: string;
  subTitle?: string;
  titleClasses?: string;
  titleLocation?: 'center' | 'bottom-left';
  cropMobile?: boolean;
  customIcon?: React.ReactNode;
  videoClasses?: string;
  wrapperClasses?: string;
}

export const VideoClip: React.FC<VideoClipProps> = ({
  src,
  srcWebm,
  poster,
  autoplay = false,
  controls = false,
  loop = true,
  muted = true,
  width = '100%',
  height = 'auto',
  title,
  subTitle,
  titleClasses,
  titleLocation = 'center',
  cropMobile = true,
  customIcon = null,
  videoClasses,
  wrapperClasses,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoplay && muted);

  const handleOnClick = () => {
    if (!controls && !autoplay) {
      setIsPlaying((prev) => !prev);
    }
  };

  useEffect(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying]);

  return (
    <m.div
      initial="hidden"
      whileInView="visible"
      viewport={viewPort}
      variants={containerVariants}
      onClick={() => handleOnClick()}
      className={`relative overflow-hidden ${cropMobile ? 'max-sm:pt-[100%]' : 'w-full h-full'} ${wrapperClasses || ''}`}
    >
      {!controls && !isPlaying ? (
        <div className="w-full h-full absolute flex justify-center items-center bg-black bg-opacity-20 z-30 top-0">
          <button aria-label="Play video" className="cursor-pointer">
            {customIcon ? (
              customIcon
            ) : (
              <svg viewBox="0 0 512 512" fill="#fff" height="8em" width="8em">
                <path d="M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm-56 296V168l144 88z" />
              </svg>
            )}
          </button>
        </div>
      ) : null}
      <video
        ref={videoRef}
        poster={poster}
        autoPlay={autoplay}
        controls={controls}
        loop={loop}
        muted={muted}
        width={width}
        height={height}
        playsInline
        className={`${videoClasses ? videoClasses : cropMobile ? 'max-sm:absolute min-h-screen max-sm:top-1/2 max-sm:left-1/2 max-sm:-translate-x-1/2 max-sm:-translate-y-1/2 max-sm:w-auto max-sm:max-w-none max-sm:h-full' : ''}`}
      >
        {srcWebm && <source src={`${srcWebm}#t=0.001`} type="video/webm" />}
        <source src={`${src}#t=0.001`} type="video/mp4" />
      </video>
      {title ? (
        <m.div
          initial="hidden"
          whileInView="visible"
          variants={containerVariants}
          viewport={viewPort}
          className={`absolute p-2 text-center text-white ${titleLocation === 'bottom-left' ? 'left-24 bottom-20' : 'grid gap-4 w-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'}`}
        >
          <m.h1 className={`${titleClasses || ''}`} variants={itemVariants}>
            {title}
          </m.h1>
          {subTitle ? (
            <m.p variants={itemVariants} className="text-xl sm:text-2xl">
              {subTitle}
            </m.p>
          ) : null}
        </m.div>
      ) : null}
    </m.div>
  );
};
